// DocumentAcknowledgment.js
// Reusable forced-read acknowledgment wrapper used for Terms, Privacy Policy,
// and any other document that requires a scroll-to-bottom + checkbox pattern.
//
// Props:
//   title          (string)       — document heading
//   content        (React node)   — content rendered inside the scrollable box
//   checkboxLabel  (string)       — text for the acknowledgment checkbox
//   firestoreField (string)       — Firestore field to set true on accept
//                                   timestamp saved as `${firestoreField}At`
//   redirectPath   (string)       — where to navigate after acknowledging
//   pdfUrl         (string, opt)  — shows a "Download PDF" button when provided
//
// Reuses .ack-* CSS classes from App.css (defined alongside HandbookAcknowledgment).

import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

function DocumentAcknowledgment({
  title,
  content,
  checkboxLabel,
  firestoreField,
  redirectPath,
  pdfUrl,
}) {
  const navigate = useNavigate();

  // ===== AUTH =====
  const [currentUser, setCurrentUser] = useState(null);

  // ===== SCROLL DETECTION =====
  const [hasScrolled, setHasScrolled] = useState(false);

  // ===== CHECKBOX =====
  const [checked, setChecked] = useState(false);

  // ===== SUBMISSION =====
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // ===== FADE IN =====
  const [visible, setVisible] = useState(false);
  useEffect(() => { setTimeout(() => setVisible(true), 50); }, []);

  // ===== AUTH LISTENER =====
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) { navigate('/login'); return; }
      setCurrentUser(user);
    });
    return () => unsubscribe();
  }, [navigate]);

  // ===== SCROLL HANDLER =====
  // Unlocks the checkbox once the user scrolls to within 10px of the bottom
  const handleScroll = (e) => {
    if (hasScrolled) return;
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 10) {
      setHasScrolled(true);
    }
  };

  // ===== ACKNOWLEDGE HANDLER =====
  const handleAgree = async () => {
    if (!checked || saving || !currentUser) return;

    setSaving(true);
    setError('');

    try {
      // Dynamic computed property names so any firestoreField value works
      await updateDoc(doc(db, 'users', currentUser.uid), {
        [firestoreField]:          true,
        [`${firestoreField}At`]:   new Date().toISOString(),
      });
      navigate(redirectPath);
    } catch (err) {
      console.error('DocumentAcknowledgment write error:', err);
      setError('Something went wrong. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div
      className="ack-page"
      style={{ opacity: visible ? 1 : 0, transition: 'opacity 0.4s ease' }}
    >
      <a href="/" className="auth-logo">Evermark Memorial</a>

      <div className="ack-card">

        {/* ===== HEADER ===== */}
        <h2 className="ack-title">{title}</h2>
        <p className="ack-subtitle">
          Please read the entire document before continuing. Scroll to the bottom to acknowledge.
        </p>

        {/* ===== ACTION BUTTONS (download + print) ===== */}
        <div className="ack-action-row">
          {/* PDF download — only rendered when pdfUrl prop is provided */}
          {pdfUrl && (
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="ack-download-btn"
            >
              ↓ Download PDF
            </a>
          )}

          {/* Print — always available; uses browser native print dialog */}
          <button
            type="button"
            className="ack-print-btn"
            onClick={() => window.print()}
          >
            Print
          </button>
        </div>

        {/* ===== SCROLL PROGRESS INDICATOR ===== */}
        <div className={`ack-scroll-indicator${hasScrolled ? ' ack-scroll-indicator--done' : ''}`}>
          {hasScrolled
            ? '✓ You have read the document'
            : 'Scroll to the bottom to enable the checkbox'}
        </div>

        {/* ===== SCROLLABLE CONTENT BOX ===== */}
        <div className="ack-content-box" onScroll={handleScroll}>
          {content}
          {/* Bottom sentinel — reaching here proves the user scrolled the full document */}
          <div className="ack-content-end">— End of Document —</div>
        </div>

        {/* ===== CHECKBOX ===== */}
        {/* Pointer-events disabled and grayed until hasScrolled is true */}
        <label className={`ack-checkbox-label${!hasScrolled ? ' ack-checkbox-label--disabled' : ''}`}>
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => { if (hasScrolled) setChecked(e.target.checked); }}
            disabled={!hasScrolled}
          />
          <span>{checkboxLabel}</span>
        </label>

        {/* Error */}
        {error && <p className="agreement-error">{error}</p>}

        {/* ===== CONTINUE BUTTON ===== */}
        {/* Disabled until checkbox is checked */}
        <button
          className={`agreement-btn${checked && !saving ? '' : ' agreement-btn--disabled'}`}
          onClick={handleAgree}
          disabled={!checked || saving}
        >
          {saving ? 'Saving...' : 'I Agree & Continue'}
        </button>

      </div>
    </div>
  );
}

export default DocumentAcknowledgment;
